import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ShoppingCart from '../components/ShoppingCart';
import ProductFilter from '../components/ProductFilter';
import CheckoutForm from '../components/CheckoutForm';
import { motion } from 'framer-motion';
import { useLanguage, t } from '../utils/i18n';
import client from '../utils/sanityClient';
import { useCart } from '../contexts/CartContext';

const StorePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]); // All products from Sanity
  const [categories, setCategories] = useState([]); // Categories from Sanity
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [priceRange, setPriceRange] = useState(10000); // Will be updated
  const [maxPrice, setMaxPrice] = useState(10000); // Max price for filter
  const [sortOption, setSortOption] = useState('featured');
  const [isLoading, setIsLoading] = useState(true);
  const { language } = useLanguage();
  
  // Use global cart context
  const { 
    cartItems, 
    showCart, 
    setShowCart,
    alertInfo,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    clearCart
  } = useCart();
  
  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9; // Show 9 items per page
  
  const navigate = useNavigate();
  
  // Get category from URL if present
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);
  
  // Load products and categories data from SANITY
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Fetch products (same query as CatalogPage)
        const sanityProducts = await client.fetch(`
          *[_type == "product"] { 
            _id,
            name,
            "slug": slug.current,
            price,
            description,
            stock,
            "category": category->name,
            images[]{ _key, alt, principal, asset->{_id, url} }
          }
        `);

        // Fetch categories (same query as CatalogPage)
        const sanityCategories = await client.fetch(`
          *[_type == "category"] | order(name asc) { _id, name, "slug": slug.current }
        `);
        
        // Map Sanity products
        const mappedProducts = sanityProducts.map(product => {
          const mainImage = product.images?.find(img => img.principal) || product.images?.[0];
          return {
            id: product._id,
            name: product.name,
            slug: product.slug,
            image: mainImage?.asset?.url || '/assets/icons/placeholder.png',
            price: product.price || 0,
            material: product.description || 'Material no especificado',
            category: product.category,
            stock: product.stock || 0,
            _original: product 
          };
        });

        setProducts(mappedProducts);
        setCategories(sanityCategories || []);
        
        // Set initial price range
        if (mappedProducts.length > 0) {
          const highestPrice = Math.max(...mappedProducts.map(p => p.price));
          const roundedMaxPrice = Math.ceil(highestPrice / 1000) * 1000 || 10000;
          setMaxPrice(roundedMaxPrice);
          setPriceRange(roundedMaxPrice);
        } else {
          setMaxPrice(10000);
          setPriceRange(10000);
        }

      } catch (error) {
        console.error('Failed to load data from Sanity:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);
  
  // Alert timer is now handled by CartContext
  
  // Filter and sort products - RESTORED TO FULL VERSION
  useEffect(() => {
    let result = [...products];
    // Apply category filter
    if (selectedCategory) {
      result = result.filter(p => p.category === selectedCategory);
    }
    // Apply price filter
    result = result.filter(p => p.price <= priceRange);
    // Apply sorting
    switch (sortOption) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'name-asc': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      case 'name-desc': result.sort((a, b) => b.name.localeCompare(a.name)); break;
      default: break; // 'featured'
    }
    setFilteredProducts(result);
    setCurrentPage(1); // Reset to first page when products/filters change
  }, [products, selectedCategory, priceRange, sortOption]); // RESTORED dependencies
  
  const handleCategorySelect = (categoryName) => {
    setSelectedCategory(categoryName);
    if (categoryName) { setSearchParams({ category: categoryName }); } 
    else { setSearchParams({}); }
  };

  // Cart functions are now provided by CartContext

  // --- Pagination Logic ---
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top on page change
  };

  // --- Helper function to get setter for sortOption ---
  // Need this because ProductFilter expects onSortOptionChange, not setSortOption directly
  const handleSortOptionChange = (newSortOption) => {
     setSortOption(newSortOption);
  };

  // Render Checkout Form if showCheckout is true
  if (showCheckout) {
    return (
      <div className="relative min-h-screen text-white">
        <div className="absolute inset-0 bg-cover bg-center z-0" style={{ backgroundImage: 'url("/assets/rain.jpg")' }} />
        <div className="absolute inset-0 bg-black/60 z-0" />
        <div className="relative z-10 py-12">
          <CheckoutForm 
            cartItems={cartItems}
            onCheckoutComplete={(orderDetails) => {
              // TODO: Process the order
              console.log('Order completed:', orderDetails);
              clearCart(); // Clear cart using context
              setShowCheckout(false); // Close checkout
              // setAlertInfo will be handled by the payment processing
            }}
            onCancel={() => setShowCheckout(false)}
          />
        </div>
      </div>
    );
  }

  // Main Store Page Render
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F2EBE6] to-[#D5BCA3]">
      {/* REMOVED Old Background Image and Overlay */}
      {/* <div className="absolute inset-0 bg-cover bg-center z-0" style={{ backgroundImage: 'url("/assets/rain.jpg")' }} /> */}
      {/* <div className="absolute inset-0 bg-black/60 z-0" /> */}

      {/* Content Area - ensure it's relative and has a z-index if needed, or remove relative from here if children handle it */}
      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-bold mb-8 text-center text-akasha-brown"
        >
          {t('store.title', language.code)}
        </motion.h1>

        {/* Grid for Sidebar + Products */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">

          {/* Sidebar (Filters) */}
          <div className="md:col-span-1">
             <div className="p-6 rounded-[2rem] bg-black/5 backdrop-blur-md border border-black/10 shadow-lg">
               <ProductFilter
                 categories={categories} 
                 selectedCategory={selectedCategory}
                 onCategoryChange={handleCategorySelect}
                 priceRange={priceRange}
                 maxPrice={maxPrice}
                 onPriceRangeChange={setPriceRange}
                 sortOption={sortOption}
                 onSortOptionChange={handleSortOptionChange}
                 language={language.code}
               />
             </div>
          </div>

          {/* Main Content (Products Grid + Pagination) */}
          <div className="md:col-span-3">

            {/* Existing Store Items Grid - Now inside col-span-3 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 min-h-[50vh]"> {/* Adjusted gap slightly */}
              {isLoading ? (
                // Loading Skeleton Grid 
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(itemsPerPage)].map((_, index) => (
                    <div key={index} className="rounded-[2rem] overflow-hidden bg-black/5 backdrop-blur-md border border-black/10 shadow-lg">
                      <div className="animate-pulse">
                        <div className="h-48 bg-neutral-700/20"></div>
                        <div className="p-3">
                          <div className="h-5 bg-neutral-600/20 rounded w-3/4 mb-2"></div>
                          <div className="h-4 bg-neutral-600/20 rounded w-1/2 mb-2"></div>
                          <div className="h-4 bg-neutral-600/20 rounded w-2/3"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {currentItems.map((item, index) => {
                    return (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="border border-black/10 rounded-lg overflow-hidden shadow-lg bg-black/5 backdrop-blur-md flex flex-col cursor-pointer group"
                        onClick={() => navigate(`/product/${item.slug}`)}
                      >
                        <div className="relative overflow-hidden">
                          <img 
                            src={item.image} 
                            alt={item.name} 
                            className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110" 
                          />
                          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </div>
                        <div className="p-3 text-akasha-black flex flex-col justify-between flex-grow">
                          <div>
                            <h2 className="text-lg font-semibold mb-1 text-akasha-black">{item.name}</h2>
                            <p className="text-gray-700 mb-2 text-xs line-clamp-2">{item.material}</p>
                          </div>
                          <div className="mt-auto">
                            <p className="text-md font-bold mb-2 text-akasha-black">${item.price.toFixed(2)}</p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                addToCart(item);
                              }}
                              className="w-full text-akasha-brown font-bold py-2 px-3 rounded-lg text-sm transition duration-300 border border-akasha-brown hover:bg-akasha-brown hover:text-white bg-white/20 backdrop-blur-sm"
                            >
                              {t('store.product.buyButton', language.code)}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                  {currentItems.length === 0 && !isLoading && (
                     <div className="col-span-full text-center py-10 text-akasha-black/80">
                         <p>{t('catalog.noProducts', language.code)}</p>
                         <button
                           onClick={() => {
                             setSelectedCategory(null);
                             setPriceRange(maxPrice);
                             setSortOption('featured');
                             setSearchParams({});
                           }}
                           className="mt-4 text-akasha-brown hover:text-akasha-brown/80 underline transition-colors"
                         >
                           {t('catalog.clearFilters', language.code)}
                         </button>
                     </div>
                  )}
                </>
              )}
            </div>

            {/* Existing Pagination Controls - Now inside col-span-3 */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-4 mt-12">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded bg-akasha-brown/60 hover:bg-akasha-brown/80 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
                >
                  {t('pagination.previous', language.code)}
                </button>
                <span className="text-akasha-black">
                  {t('pagination.page', language.code)} {currentPage} {t('pagination.of', language.code)} {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded bg-akasha-brown/60 hover:bg-akasha-brown/80 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
                >
                  {t('pagination.next', language.code)}
                </button>
              </div>
            )}
          </div> {/* End Main Content Column */}
        </div> {/* End Grid for Sidebar + Products */}

        {/* Floating Cart Button */}
        {cartItems.length > 0 && (
          <div className="fixed bottom-4 right-4 z-20">
            <motion.button
              onClick={() => setShowCheckout(true)}
              className="text-white font-bold py-3 px-6 rounded-full shadow-lg transition duration-300 flex items-center bg-akasha-brown hover:bg-akasha-brown/80"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {t('cart.button', language.code)} ({t('cart.itemCount', language.code, { count: cartItems.reduce((total, item) => total + item.quantity, 0) })})
            </motion.button>
          </div>
        )}

        {/* Add to Cart Alert */}
        {alertInfo && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg text-white ${alertInfo.type === 'success' ? 'bg-green-600' : 'bg-yellow-600'
              }`}
            role="alert"
          >
            {alertInfo.message}
          </motion.div>
        )}
      </div>
      
      {/* Shopping Cart Sidebar - Style Internally? */}
      <ShoppingCart 
        isOpen={showCart}
        onClose={() => setShowCart(false)}
        items={cartItems}
        onUpdateQuantity={updateCartItemQuantity}
        onRemoveItem={removeFromCart}
        onCheckout={() => {
          setShowCart(false);
          setShowCheckout(true);
        }}
        language={language.code} // Pass language for labels inside
      />
    </div>
  );
};

export default StorePage;
